import _ from 'lodash'
import Wizard from 'scoreboard/components/wizard'
import ViSimpleAdvanced from './vi-simple-advanced'
import ViSimple from './vi-simple'
import DbConnection from '../db-connection'
import WorksheetOptions from 'scoreboard/imports/worksheet-options'
import ViSourceData from './vi-source-data'
import ViTransformations from './vi-transformations'
import ViDestinationItems from './vi-destination-items'
import ViMapping from './vi-mapping'
import LabelOwnersSchedule from '../label-owners-schedule'
import SpreadsheetChooser from 'scoreboard/imports/spreadsheet-chooser'
import RestRequestForm from 'scoreboard/imports/rest/rest-request-form'
import viDialog from './vi-dialog'
import Import from 'scoreboard/imports/model/import'
import user from 'scoreboard/components/user'
import unwrapModel from 'scoreboard/components/unwrap-model'
import DataSourceInfoWizard from '../data-source-info-wizard'
import DataSourceTypeChooser from 'scoreboard/components/import-data-source/data-source-type-chooser'
import constants from 'scoreboard/components/constants'
const { REST } = constants
import RestDataSelector from '../rest/rest-data-selector'

let simpleViews = [
	ViSimpleAdvanced,
	ViSimple
]

// views when using a database for the data source
const dbViews = advancedViews([
	DbConnection
])

// views when using a spreadsheet for the data source
const spreadsheetViews = advancedViews([
	SpreadsheetChooser,
	WorksheetOptions
])

const restViews = advancedViews([
	RestRequestForm,
	RestDataSelector
])

/**
 * Returns all the views for an advanced import
 * @param middleViews the views in the middle of the wizard. These are the
 * only ones that vary between different types of advanced imports
 */
function advancedViews(middleViews) {
	return [ViSimpleAdvanced, DataSourceTypeChooser]
		.concat(middleViews)
		.concat([
			ViTransformations,
			ViSourceData,
			ViDestinationItems,
			ViMapping,
			LabelOwnersSchedule
		])
}

const ViWizard = DataSourceInfoWizard.extend({

	/**
	 * Wizard for setting up a metric value import
	 *
	 * @constructs
	 *
	 * @param {Object} [options]
	 *
	 * @param {Import} options.model
	 *
	 * @param {Array} options.connections array of connections the user either
	 * has access to or can view because they are used by one of the imports the
	 * user has access to
	 */
	initialize: function (options = {}) {
		DataSourceInfoWizard.prototype.initialize.apply(this, arguments)
	},

	next: function () {
		this.setViews(this.getViews())
		Wizard.prototype.next.apply(this, arguments)
	},

	/**
	 * Updates views as appropriate based on the "source" (spreadsheet, db, etc)
	 */
	getViews: function(advanced) {
		let model = unwrapModel(this.options.model)
		advanced = _.isNil(advanced) ? _.get(model, 'advanced') : advanced
		advanced = _.isUndefined(advanced) ? false : advanced
		if (!advanced) {
			return simpleViews
		}

		let source = _.get(model, 'dataSourceMetadata.source')
		if (source === 'database') {
			return dbViews
		}
		if (source === REST) {
			return restViews
		}
		// there won't be a source specified for new imports, so be sure to
		// return some views in that case
		return spreadsheetViews
	},

	/**
	 * Update the wizard's views as needed, based on the data source type
	 * selected in the DataSourceTypeChooser step
	 */
	changeViews: function(selectedDataSourceType) {
		let views
		let { groupType} = selectedDataSourceType || {}
		if (groupType === 'spreadsheet') {
			views = spreadsheetViews
		} else if (groupType === 'database') {
			views = dbViews
		}
		if (_.isNil(views)) return

		this.setViews(views)
		this.render()
	},

	cancel: function() {
		// delete file on cancel for new imports only
		// only delete file if it was uploaded, not if a managed document was selected
		if (this.options.model.isNew() && _.isNil(this.options.model.get('documentId'))) {
			this.model.deleteFile()
		}
	},

	finish: function(action) {
		// trigger specific action, if specified
		// (ex 'import-complete' to indicate the import was run but not saved)
		this.trigger(action || 'done', this.options.model)
	}

}, {

	/**
	 * Starts the import wizard
	 * @param [importId] if editing an existing import, the ID of the import
	 * @param [type] if creating a new import, the type of import (either
	 * 'metric-value' or 'initiative-status'
	 * @returns {ViWizard} the wizard
	 */
	start: function({ importId, type, doneKey }) {
		let wizard = new ViWizard()
		viDialog({importId, statuses: true}, data => {
			let lastNavigable = _.isNil(importId) ? 1 : Number.MAX_VALUE
			let model
			if (_.isNil(importId)) {
				model = new Import({
					type,
					owners: [user.principal]
				})
			} else {
				model = new Import(data.import)
			}

			wizard.initialize({
				model,
				connections: data.connections,
				lastNavigable,
				doneKey,
				useViInstructions: true
			})
			return wizard._view()
		})
		return wizard
	},

	/**
	 * The column mapping options available on the source data step
	 */
	COLUMN_MAPPING_OPTIONS: [
		{ id: 'name', label: 'admin.import.metricIdName', multiple: true },
		{ id: 'date', label: 'common.date' },
		{ id: 'value', label: 'common.value', multiple: true },
		{ id: 'threshold', label: 'scorecards.node.metric.threshold', multiple: true },
		{ id: 'note', label: 'common.note.note' }
	],

	columnMappingOptionById: function(id) {
		if (!this._columnMappingOptionsById) {
			this._columnMappingOptionsById = _.keyBy(this.COLUMN_MAPPING_OPTIONS, 'id')
		}
		return this._columnMappingOptionsById[id]
	}

})

export default ViWizard

ViWizard.__filename__ = 'imports/vi/vi-wizard.js'
